// TypeScript interfaces for CatchesPage redesign

import { CatchLog } from './FishingEntities';

// Filter types
export interface CatchFilters {
  search: string;
  dateRange: '' | 'today' | 'week' | 'month' | 'year';
  species: string[]; // Array of species keys
  areas: string[]; // Array of area IDs
  caughtOnly: boolean | null; // null = all, true = caught only, false = contact only
  minWeight?: number; // Minimum weight in grams
  maxWeight?: number; // Maximum weight in grams
  minLength?: number; // Minimum length in mm
  maxLength?: number; // Maximum length in mm
  methods: string[]; // Fishing methods (spinning, trolling, fly, etc)
  baits: string[]; // Baits/lures used
  seasons: string[]; // Spring, Summer, Fall, Winter
  timeOfDay?: { start: number; end: number }; // Hour range 0-23
  moonPhase?: { min: number; max: number }; // 0-1 (new to full)
  minPressure?: number; // hPa
  maxPressure?: number; // hPa
  minTemp?: number; // Celsius
  maxTemp?: number; // Celsius
  minWindSpeed?: number; // m/s
  maxWindSpeed?: number; // m/s
  windDirections: string[]; // N, NE, E, SE, S, SW, W, NW
}

// Sort options
export type SortOption = 'newest' | 'oldest' | 'weight-desc' | 'length-desc';

// Group options
export type GroupOption = 'date' | 'area' | 'species' | 'session' | 'season' | 'none';

// View modes
export type ViewMode = 'list' | 'grid' | 'map';

// Group stats
export interface GroupStats {
  totalCatches: number;
  caughtCount: number;
  contactCount: number;
  totalWeight: number;
  speciesBreakdown: Array<{ species: string; count: number }>;
}

// Grouped catches
export interface GroupedCatches {
  groupKey: string;
  groupTitle: string;
  catches: CatchLog[];
  stats: GroupStats;
}

// Species with translation
export interface SpeciesWithTranslation {
  key: string;
  name: string;
  isCustom: boolean;
}

// Helper functions
export function getDefaultFilters(): CatchFilters {
  return {
    search: '',
    dateRange: '',
    species: [],
    areas: [],
    caughtOnly: null,
    minWeight: undefined,
    maxWeight: undefined,
    minLength: undefined,
    maxLength: undefined,
    methods: [],
    baits: [],
    seasons: [],
    timeOfDay: undefined,
    moonPhase: undefined,
    minPressure: undefined,
    maxPressure: undefined,
    minTemp: undefined,
    maxTemp: undefined,
    minWindSpeed: undefined,
    maxWindSpeed: undefined,
    windDirections: []
  };
}

export function calculateGroupStats(catches: CatchLog[]): GroupStats {
  const stats: GroupStats = {
    totalCatches: catches.length,
    caughtCount: catches.filter(c => c.caught).length,
    contactCount: catches.filter(c => !c.caught).length,
    totalWeight: catches.reduce((sum, c) => sum + (c.weight || 0), 0),
    speciesBreakdown: []
  };
  
  // Calculate species breakdown
  const speciesMap = new Map<string, number>();
  catches.forEach(c => {
    const count = speciesMap.get(c.species) || 0;
    speciesMap.set(c.species, count + 1);
  });
  
  stats.speciesBreakdown = Array.from(speciesMap.entries())
    .map(([species, count]) => ({ species, count }))
    .sort((a, b) => b.count - a.count); // Sort by count descending
  
  return stats;
}

export function filterCatches(catches: CatchLog[], filters: CatchFilters): CatchLog[] {
  let filtered = [...catches];
  
  // Search filter (species, notes)
  if (filters.search) {
    const searchLower = filters.search.toLowerCase();
    filtered = filtered.filter(c => 
      c.species.toLowerCase().includes(searchLower) ||
      c.notes?.toLowerCase().includes(searchLower)
    );
  }
  
  // Date range filter
  if (filters.dateRange) {
    const now = Date.now();
    const oneDayMs = 24 * 60 * 60 * 1000;
    
    let minTimestamp = 0;
    switch (filters.dateRange) {
      case 'today':
        minTimestamp = now - oneDayMs;
        break;
      case 'week':
        minTimestamp = now - 7 * oneDayMs;
        break;
      case 'month':
        minTimestamp = now - 30 * oneDayMs;
        break;
      case 'year':
        minTimestamp = now - 365 * oneDayMs;
        break;
    }
    
    filtered = filtered.filter(c => c.timestamp >= minTimestamp);
  }
  
  // Species filter
  if (filters.species.length > 0) {
    filtered = filtered.filter(c => filters.species.includes(c.species));
  }
  
  // Area filter
  if (filters.areas.length > 0) {
    filtered = filtered.filter(c => c.fishingAreaId && filters.areas.includes(c.fishingAreaId));
  }
  
  // Caught/Contact filter
  if (filters.caughtOnly !== null) {
    filtered = filtered.filter(c => c.caught === filters.caughtOnly);
  }
  
  // Weight filter
  if (filters.minWeight !== undefined) {
    filtered = filtered.filter(c => c.weight && c.weight >= filters.minWeight!);
  }
  if (filters.maxWeight !== undefined) {
    filtered = filtered.filter(c => c.weight && c.weight <= filters.maxWeight!);
  }
  
  // Length filter
  if (filters.minLength !== undefined) {
    filtered = filtered.filter(c => c.length && c.length >= filters.minLength!);
  }
  if (filters.maxLength !== undefined) {
    filtered = filtered.filter(c => c.length && c.length <= filters.maxLength!);
  }
  
  return filtered;
}

export function sortCatches(catches: CatchLog[], sortBy: SortOption): CatchLog[] {
  const sorted = [...catches];
  
  switch (sortBy) {
    case 'newest':
      sorted.sort((a, b) => b.timestamp - a.timestamp);
      break;
    case 'oldest':
      sorted.sort((a, b) => a.timestamp - b.timestamp);
      break;
    case 'weight-desc':
      sorted.sort((a, b) => (b.weight || 0) - (a.weight || 0));
      break;
    case 'length-desc':
      sorted.sort((a, b) => (b.length || 0) - (a.length || 0));
      break;
  }
  
  return sorted;
}

export function groupCatches(catches: CatchLog[], groupBy: GroupOption): GroupedCatches[] {
  if (groupBy === 'none') {
    return [{
      groupKey: 'all',
      groupTitle: 'All Catches',
      catches,
      stats: calculateGroupStats(catches)
    }];
  }
  
  const groups = new Map<string, CatchLog[]>();
  
  catches.forEach(catchLog => {
    let key = '';
    
    switch (groupBy) {
      case 'date':
        // Group by month (YYYY-MM)
        const date = new Date(catchLog.timestamp);
        key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        break;
      case 'area':
        key = catchLog.fishingAreaId || 'no-area';
        break;
      case 'species':
        key = catchLog.species;
        break;
      case 'session':
        key = catchLog.sessionId || 'no-session';
        break;
      case 'season':
        // Group by season based on month
        const seasonDate = new Date(catchLog.timestamp);
        const month = seasonDate.getMonth();
        if (month >= 2 && month <= 4) key = 'spring'; // Mar-May
        else if (month >= 5 && month <= 7) key = 'summer'; // Jun-Aug
        else if (month >= 8 && month <= 10) key = 'fall'; // Sep-Nov
        else key = 'winter'; // Dec-Feb
        break;
    }
    
    if (!groups.has(key)) {
      groups.set(key, []);
    }
    groups.get(key)!.push(catchLog);
  });
  
  // Convert to array and calculate stats
  return Array.from(groups.entries()).map(([key, catches]) => ({
    groupKey: key,
    groupTitle: getGroupTitle(key, groupBy, catches),
    catches,
    stats: calculateGroupStats(catches)
  }));
}

function getGroupTitle(key: string, groupBy: GroupOption, catches: CatchLog[]): string {
  switch (groupBy) {
    case 'date':
      // Convert YYYY-MM to month name
      const [year, month] = key.split('-');
      const date = new Date(parseInt(year), parseInt(month) - 1);
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
    case 'area':
      if (key === 'no-area') return 'No Location';
      // Area name will be fetched from FishingArea by the component
      return key;
    case 'species':
      return key;
    case 'session':
      if (key === 'no-session') return 'No Session';
      return `Session ${key.substring(0, 8)}`;
    case 'season':
      // Return season with icon
      switch (key) {
        case 'spring': return '🌸 Spring';
        case 'summer': return '☀️ Summer';
        case 'fall': return '🍂 Fall';
        case 'winter': return '❄️ Winter';
        default: return key;
      }
    default:
      return key;
  }
}
